import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'src/app/core';
import {
  getImageUploadValidationError,
  IMAGE_UPLOAD_ACCEPT
} from 'src/app/core/constants/image-upload.constant';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';

interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ImageLayout {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  scale: number;
}

type ImageResizeHandle = 'nw' | 'ne' | 'se' | 'sw';
type DragType = 'image' | 'selection' | 'image-resize';
type NudgeDirection = 'up' | 'down' | 'left' | 'right';
type LogoShapeMode = 'square' | 'rectangle';
type CanvasMode = 'light' | 'dark' | 'transparent';

interface Point {
  x: number;
  y: number;
}

/**
 * Canvas-based image cropper: themed stage, image layer, movable selection overlay.
 * Export always uses targetWidth x targetHeight (labels match export). Small square
 * targets (favicon) may use a larger selection/preview for usability only — logos
 * stay 1:1 with the export size — see README.md in this folder.
 */
@Component({
  selector: 'app-image-cropper-modal',
  templateUrl: './image-cropper-modal.component.html',
  styleUrls: ['./image-cropper-modal.component.scss'],
  standalone: false
})
export class ImageCropperModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('stageCanvas') stageCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('previewCanvas') previewCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('workspace') workspace?: ElementRef<HTMLDivElement>;
  @ViewChild('replacementImageInput') replacementImageInput?: ElementRef<HTMLInputElement>;

  private _imageFile: File | null = null;
  private sourceImage: HTMLImageElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private themeObserver: MutationObserver | null = null;
  private previewUpdateRafId: number | null = null;
  private stageRenderRafId: number | null = null;
  private finalSizeUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  private nudgeRepeatTimer: ReturnType<typeof setTimeout> | null = null;
  private nudgeRepeatInterval: ReturnType<typeof setInterval> | null = null;
  private heldNudgeKey: NudgeDirection | null = null;
  private initialImageLayout: ImageLayout | null = null;
  private initialSelection: CropRect | null = null;
  private dragState: {
    type: DragType;
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    originLayout?: ImageLayout;
    resizeHandle?: ImageResizeHandle;
  } | null = null;

  private stageWidth = 0;
  private stageHeight = 0;
  private devicePixelRatio = 1;

  title = 'Image Crop & Preview';
  aspectRatio: number | null = null;
  maintainAspectRatio = false;
  roundCropper = false;
  targetWidth = 0;
  targetHeight = 0;
  /**
   * Logo-only: lets users switch between square (250×250) and rectangle (e.g. 280×80)
   * export inside the cropper without changing client-page preview frames.
   */
  allowLogoShapeSwitch = false;
  /** Default square export for clients with square logos — cropper/export only. */
  readonly logoSquareExportSize = 250;
  logoShapeMode: LogoShapeMode = 'square';
  /** Rectangle dimensions passed from the parent upload (restored when shape = rectangle). */
  private rectangleTargetWidth = 0;
  private rectangleTargetHeight = 0;
  private rectangleAspectRatio: number | null = null;
  /**
   * When enabled, export keeps alpha instead of baking void fill (forces PNG output).
   * Checkbox is shown only when alpha is detected or transparent canvas is active.
   */
  keepLogoTransparency = false;
  private isPngUpload = false;
  private imageHasTransparency = false;
  /** Initial canvas tone when the cropper opens (set by parent upload context). */
  defaultCanvasMode: CanvasMode = 'light';
  canvasMode: CanvasMode = 'light';

  selection: CropRect = { x: 0, y: 0, width: 280, height: 80 };
  imageLayout: ImageLayout = { x: 0, y: 0, width: 0, height: 0, rotation: 0, scale: 1 };

  moveMode: 'selection' | 'image' = 'image';
  isDragging = false;
  activeResizeHandle: ImageResizeHandle | null = null;
  isEditorReady = false;
  isApplying = false;
  isLoading = false;
  originalWidth = 0;
  originalHeight = 0;
  showHelp = false;
  finalImageSizeLabel = '-';
  activeNudgeDirection: NudgeDirection | null = null;

  zoomValue = 1;
  readonly minZoom = 0.1;
  readonly maxZoom = 5;
  readonly zoomStep = 0.1;
  readonly acceptedImageAccept = IMAGE_UPLOAD_ACCEPT;

  private readonly imageHandleSize = 8;
  private readonly imageHandleHitRadius = 10;
  private readonly minImageDisplaySize = 16;
  private readonly nudgeStepPx = 1;
  private readonly nudgeRepeatDelayMs = 320;
  private readonly nudgeRepeatIntervalMs = 45;
  private initialCanvasMode: CanvasMode = 'light';

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private msgService: MessageService
  ) {}

  get imageFile(): File | null {
    return this._imageFile;
  }

  set imageFile(file: File | null) {
    this._imageFile = file;
    this.pendingFile = file;
    void this.tryLoadPendingImage();
  }

  private pendingFile: File | null = null;

  get targetSizeLabel(): string {
    return this.effectiveTargetWidth && this.effectiveTargetHeight
      ? `${this.effectiveTargetWidth} x ${this.effectiveTargetHeight} px`
      : 'Custom size';
  }

  /** Active export dimensions — reflects square/rectangle logo mode when enabled. */
  get effectiveTargetWidth(): number {
    return this.targetWidth;
  }

  get effectiveTargetHeight(): number {
    return this.targetHeight;
  }

  /** True when PNG transparency is preserved in the final export (preview uses canvas mode separately). */
  get isTransparencyPreserved(): boolean {
    return this.showTransparencyOption && this.keepLogoTransparency;
  }

  /** Shown only when alpha is detected in the uploaded image. */
  get showTransparencyOption(): boolean {
    return this.imageHasTransparency;
  }

  /** On transparent canvas, lock the checkbox when the image actually has alpha. */
  get isTransparencyCheckboxReadOnly(): boolean {
    return this.canvasMode === 'transparent' && this.imageHasTransparency;
  }

  get isBelowRecommendedResolution(): boolean {
    if (!this.originalWidth || !this.originalHeight || !this.effectiveTargetWidth || !this.effectiveTargetHeight) {
      return false;
    }

    return this.originalWidth < this.effectiveTargetWidth
      || this.originalHeight < this.effectiveTargetHeight;
  }

  get fileNameLabel(): string {
    return this.imageFile?.name || '-';
  }

  get fileSizeLabel(): string {
    return this.formatFileSize(this.imageFile?.size || 0);
  }

  get formatLabel(): string {
    const name = this.imageFile?.name || '';
    const extension = name.includes('.') ? name.split('.').pop() || '' : '';
    return (extension || this.outputFormat).toUpperCase();
  }

  get imageSizeLabel(): string {
    if (!this.originalWidth || !this.originalHeight) {
      return '-';
    }

    return `${this.originalWidth} x ${this.originalHeight} px`;
  }

  get previewFrameAspectRatio(): string {
    if (this.roundCropper) {
      return '1';
    }

    if (this.effectiveTargetWidth && this.effectiveTargetHeight) {
      return `${this.effectiveTargetWidth} / ${this.effectiveTargetHeight}`;
    }

    return '3.5';
  }

  get aspectRatioLabel(): string {
    if (!this.aspectRatio) {
      return '';
    }
    if (this.aspectRatio === 1) {
      return '1:1';
    }
    return this.aspectRatio.toFixed(2);
  }

  get outputFormat(): 'png' | 'jpeg' {
    // Transparency and round crops require PNG — JPEG cannot store alpha.
    if (this.roundCropper || this.isTransparencyPreserved) {
      return 'png';
    }

    const name = this.imageFile?.name?.toLowerCase() ?? '';
    return name.endsWith('.png') ? 'png' : 'jpeg';
  }

  get mimeType(): string {
    return this.outputFormat === 'png' ? 'image/png' : 'image/jpeg';
  }

  ngAfterViewInit(): void {
    this.observeStageSize();
    this.observeThemeChanges();
    void this.tryLoadPendingImage();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.themeObserver?.disconnect();
    if (this.finalSizeUpdateTimer) {
      clearTimeout(this.finalSizeUpdateTimer);
    }
    this.stopNudgeRepeat();
    this.cancelScheduledPreviewUpdate();
    this.cancelScheduledStageRender();
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeyDown(event: KeyboardEvent): void {
    if (!this.isEditorReady || this.isApplying || this.isLoading) {
      return;
    }

    if (this.isTypingTarget(event.target)) {
      return;
    }

    const zoomDirection = this.getZoomDirectionFromKey(event);
    if (zoomDirection) {
      event.preventDefault();
      if (zoomDirection === 'in') {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
      return;
    }

    const direction = this.getNudgeDirectionFromKey(event.key);
    if (!direction) {
      return;
    }

    event.preventDefault();

    if (event.repeat) {
      if (this.heldNudgeKey !== direction) {
        this.heldNudgeKey = direction;
        this.activeNudgeDirection = direction;
        this.nudgeByDirection(direction);
      }
      return;
    }

    this.heldNudgeKey = direction;
    this.activeNudgeDirection = direction;
    this.nudgeByDirection(direction);
    this.beginNudgeRepeat(direction);
  }

  @HostListener('window:keyup', ['$event'])
  onWindowKeyUp(event: KeyboardEvent): void {
    const direction = this.getNudgeDirectionFromKey(event.key);
    if (!direction || this.heldNudgeKey !== direction) {
      return;
    }

    this.stopNudgeRepeat();
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    this.stopNudgeRepeat();
  }

  onNudgePointerDown(event: PointerEvent, direction: NudgeDirection): void {
    if (!this.isEditorReady || this.isApplying || this.isLoading) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.heldNudgeKey = direction;
    this.activeNudgeDirection = direction;
    this.nudgeByDirection(direction);
    this.beginNudgeRepeat(direction);

    const target = event.currentTarget as HTMLElement | null;
    target?.setPointerCapture?.(event.pointerId);
  }

  onNudgePointerUp(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.stopNudgeRepeat();
  }

  private getNudgeDirectionFromKey(key: string): NudgeDirection | null {
    switch (key) {
      case 'ArrowUp':
        return 'up';
      case 'ArrowDown':
        return 'down';
      case 'ArrowLeft':
        return 'left';
      case 'ArrowRight':
        return 'right';
      default:
        return null;
    }
  }

  private getZoomDirectionFromKey(event: KeyboardEvent): 'in' | 'out' | null {
    switch (event.key) {
      case '+':
      case '=':
        return 'in';
      case '-':
      case '_':
        return 'out';
      default:
        break;
    }

    switch (event.code) {
      case 'NumpadAdd':
        return 'in';
      case 'NumpadSubtract':
        return 'out';
      default:
        return null;
    }
  }

  private isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    const tag = target.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
  }

  private beginNudgeRepeat(direction: NudgeDirection): void {
    this.stopNudgeRepeatTimersOnly();

    this.nudgeRepeatTimer = setTimeout(() => {
      this.nudgeRepeatInterval = setInterval(() => {
        if (this.heldNudgeKey !== direction) {
          this.stopNudgeRepeat();
          return;
        }
        this.nudgeByDirection(direction);
      }, this.nudgeRepeatIntervalMs);
    }, this.nudgeRepeatDelayMs);
  }

  private stopNudgeRepeatTimersOnly(): void {
    if (this.nudgeRepeatTimer) {
      clearTimeout(this.nudgeRepeatTimer);
      this.nudgeRepeatTimer = null;
    }
    if (this.nudgeRepeatInterval) {
      clearInterval(this.nudgeRepeatInterval);
      this.nudgeRepeatInterval = null;
    }
  }

  private stopNudgeRepeat(): void {
    this.stopNudgeRepeatTimersOnly();
    this.heldNudgeKey = null;
    this.activeNudgeDirection = null;
  }

  private nudgeByDirection(direction: NudgeDirection): void {
    const delta = this.getNudgeDelta(direction);
    if (this.moveMode === 'selection') {
      this.nudgeSelection(delta.x, delta.y);
    } else {
      this.nudgeImage(delta.x, delta.y);
    }
    this.scheduleStageRender();
  }

  private scheduleStageRender(): void {
    if (this.stageRenderRafId !== null) {
      return;
    }

    this.stageRenderRafId = requestAnimationFrame(() => {
      this.stageRenderRafId = null;
      this.render();
    });
  }

  private cancelScheduledStageRender(): void {
    if (this.stageRenderRafId !== null) {
      cancelAnimationFrame(this.stageRenderRafId);
      this.stageRenderRafId = null;
    }
  }

  private getNudgeDelta(direction: NudgeDirection): Point {
    switch (direction) {
      case 'up':
        return { x: 0, y: -this.nudgeStepPx };
      case 'down':
        return { x: 0, y: this.nudgeStepPx };
      case 'left':
        return { x: -this.nudgeStepPx, y: 0 };
      case 'right':
        return { x: this.nudgeStepPx, y: 0 };
    }
  }

  private nudgeSelection(deltaX: number, deltaY: number): void {
    this.selection = {
      ...this.selection,
      x: this.clamp(this.selection.x + deltaX, 0, this.stageWidth - this.selection.width),
      y: this.clamp(this.selection.y + deltaY, 0, this.stageHeight - this.selection.height)
    };
  }

  private nudgeImage(deltaX: number, deltaY: number): void {
    this.imageLayout = {
      ...this.imageLayout,
      x: this.imageLayout.x + deltaX,
      y: this.imageLayout.y + deltaY
    };
  }

  getCroppedFileName(): string {
    const original = this.imageFile?.name || 'image';
    const baseName = original.includes('.')
      ? original.substring(0, original.lastIndexOf('.'))
      : original;
    const extension = this.outputFormat === 'png' ? 'png' : 'jpg';
    return `${baseName}.${extension}`;
  }

  setMoveMode(mode: 'selection' | 'image'): void {
    this.moveMode = mode;
    this.activeResizeHandle = null;
    this.stopNudgeRepeat();
    this.endDrag();
    this.render();
  }

  setCanvasMode(mode: CanvasMode): void {
    if (this.canvasMode === mode) {
      return;
    }

    this.canvasMode = mode;

    if (mode === 'transparent' && this.imageHasTransparency) {
      // Transparent canvas + alpha image: preserve transparency and lock the checkbox.
      this.keepLogoTransparency = true;
    } else if (mode === 'transparent') {
      this.keepLogoTransparency = false;
    } else if (!this.isPngUpload) {
      this.keepLogoTransparency = false;
    } else {
      this.keepLogoTransparency = this.imageHasTransparency;
    }

    this.render();
  }

  /** Confirm before flattening transparent areas onto the canvas background. */
  onKeepTransparencyCheckboxChange(event: Event): void {
    if (this.isTransparencyCheckboxReadOnly) {
      return;
    }

    const input = event.target as HTMLInputElement;

    if (input.checked) {
      this.keepLogoTransparency = true;
      this.render();
      return;
    }

    // Revert the native control until the user confirms — ngModel alone stays unchecked on cancel.
    input.checked = true;
    this.keepLogoTransparency = true;

    void this.confirmDisableTransparency().then((confirmed) => {
      if (confirmed) {
        this.keepLogoTransparency = false;
        this.render();
      }
    });
  }

  private confirmDisableTransparency(): Promise<boolean> {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      size: 'sm',
      backdrop: 'static',
      windowClass: 'image-cropper-confirm-modal-window'
    });

    modalRef.componentInstance.confirmationBoxTitle = 'Transparency will be lost';
    modalRef.componentInstance.confirmationMessage = this.translateService.instant(
      'Disabling transparency will flatten the image onto the selected canvas background. All transparent areas will be filled and transparency will be lost. Do you want to continue?'
    );
    modalRef.componentInstance.confirmationYesButton = this.translateService.instant('Continue');
    modalRef.componentInstance.confirmationNoButton = this.translateService.instant('Cancel');

    // Cancel / dismiss must resolve false — close(false) is not a rejection.
    return modalRef.result.then((result) => result === true).catch(() => false);
  }

  /** Logo cropper only — switches export between 250×250 square and configured rectangle. */
  setLogoShapeMode(mode: LogoShapeMode): void {
    if (!this.allowLogoShapeSwitch || this.roundCropper || this.logoShapeMode === mode) {
      return;
    }

    this.logoShapeMode = mode;
    this.applyLogoShapeTargets();
    this.selection = this.buildInitialSelection();
    this.initialSelection = { ...this.selection };
    this.render();
  }

  onCanvasModeSwitchPointerDown(event: PointerEvent): void {
    event.stopPropagation();
  }

  onLogoShapeSwitchPointerDown(event: PointerEvent): void {
    event.stopPropagation();
  }

  onZoomChange(value: number | string): void {
    const zoom = typeof value === 'string' ? parseFloat(value) : value;
    this.zoomValue = Math.min(this.maxZoom, Math.max(this.minZoom, zoom));
    this.imageLayout = { ...this.imageLayout, scale: this.zoomValue };
    this.render();
  }

  zoomIn(): void {
    this.onZoomChange(this.zoomValue + this.zoomStep);
  }

  zoomOut(): void {
    this.onZoomChange(this.zoomValue - this.zoomStep);
  }

  onWorkspaceWheel(event: WheelEvent): void {
    event.preventDefault();
    const zoomDelta = event.deltaY < 0 ? this.zoomStep : -this.zoomStep;
    this.onZoomChange(this.zoomValue + zoomDelta);
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.isEditorReady || event.button !== 0) {
      return;
    }

    const point = this.getCanvasPoint(event);

    if (this.moveMode === 'image') {
      const resizeHandle = this.hitTestImageResizeHandle(point);
      if (resizeHandle) {
        this.dragState = {
          type: 'image-resize',
          pointerId: event.pointerId,
          startX: point.x,
          startY: point.y,
          originX: 0,
          originY: 0,
          originLayout: { ...this.imageLayout },
          resizeHandle
        };
        this.isDragging = true;
        this.stageCanvas?.nativeElement.setPointerCapture(event.pointerId);
        event.preventDefault();
        return;
      }

      if (this.isPointOnImage(point)) {
        this.dragState = {
          type: 'image',
          pointerId: event.pointerId,
          startX: point.x,
          startY: point.y,
          originX: this.imageLayout.x,
          originY: this.imageLayout.y
        };
        this.isDragging = true;
        this.stageCanvas?.nativeElement.setPointerCapture(event.pointerId);
        event.preventDefault();
      }
      return;
    }

    if (this.moveMode === 'selection' && this.isPointInSelection(point)) {
      this.dragState = {
        type: 'selection',
        pointerId: event.pointerId,
        startX: point.x,
        startY: point.y,
        originX: this.selection.x,
        originY: this.selection.y
      };
      this.isDragging = true;
      this.stageCanvas?.nativeElement.setPointerCapture(event.pointerId);
      event.preventDefault();
    }
  }

  onPointerMove(event: PointerEvent): void {
    const point = this.getCanvasPoint(event);

    if (!this.dragState) {
      this.activeResizeHandle = this.moveMode === 'image'
        ? this.hitTestImageResizeHandle(point)
        : null;
      return;
    }

    if (event.pointerId !== this.dragState.pointerId) {
      return;
    }

    const deltaX = point.x - this.dragState.startX;
    const deltaY = point.y - this.dragState.startY;

    if (this.dragState.type === 'selection') {
      this.selection = {
        ...this.selection,
        x: this.clamp(this.dragState.originX + deltaX, 0, this.stageWidth - this.selection.width),
        y: this.clamp(this.dragState.originY + deltaY, 0, this.stageHeight - this.selection.height)
      };
    } else if (this.dragState.type === 'image') {
      this.imageLayout = {
        ...this.imageLayout,
        x: this.dragState.originX + deltaX,
        y: this.dragState.originY + deltaY
      };
    } else if (
      this.dragState.type === 'image-resize'
      && this.dragState.originLayout
      && this.dragState.resizeHandle
    ) {
      this.imageLayout = this.resizeImageFromHandle(
        this.dragState.resizeHandle,
        point,
        this.dragState.originLayout
      );
      this.zoomValue = this.imageLayout.scale;
    }

    this.scheduleStageRender();
  }

  onPointerUp(event: PointerEvent): void {
    if (this.dragState?.pointerId === event.pointerId) {
      this.endDrag();
    }
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    this.endDrag();
  }

  rotateLeft(): void {
    this.imageLayout = {
      ...this.imageLayout,
      rotation: this.imageLayout.rotation - 90
    };
    this.render();
  }

  rotateRight(): void {
    this.imageLayout = {
      ...this.imageLayout,
      rotation: this.imageLayout.rotation + 90
    };
    this.render();
  }

  resetAdjustments(): void {
    if (this.initialImageLayout && this.initialSelection) {
      this.imageLayout = { ...this.initialImageLayout };
      this.selection = { ...this.initialSelection };
      this.zoomValue = 1;
      this.moveMode = 'image';
      this.canvasMode = this.initialCanvasMode;
      this.render();
    }
  }

  toggleHelp(): void {
    this.showHelp = !this.showHelp;
  }

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }

  openReplacementImagePicker(): void {
    this.replacementImageInput?.nativeElement.click();
  }

  onReplacementImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const validationError = getImageUploadValidationError(file);

    if (validationError) {
      this.msgService.showError(this.translateService.instant(validationError));
      input.value = '';
      return;
    }

    if (file) {
      this.isEditorReady = false;
      this._imageFile = file;
      this.pendingFile = file;
      void this.tryLoadPendingImage();
    }

    input.value = '';
  }

  async apply(): Promise<void> {
    if (!this.imageFile || !this.isEditorReady || this.isApplying) {
      return;
    }

    this.isApplying = true;
    try {
      const blob = await this.exportCroppedBlob();
      if (!blob) {
        return;
      }

      const croppedFile = new File([blob], this.getCroppedFileName(), {
        type: this.mimeType,
        lastModified: Date.now()
      });

      this.activeModal.close(croppedFile);
    } finally {
      this.isApplying = false;
    }
  }

  private async tryLoadPendingImage(): Promise<void> {
    if (!this.pendingFile || !this.stageCanvas) {
      return;
    }

    const file = this.pendingFile;
    this.pendingFile = null;
    await this.loadImage(file);
  }

  private async loadImage(file: File | null): Promise<void> {
    this.isLoading = true;
    this.isEditorReady = false;
    this.sourceImage = null;

    if (!file) {
      this.isLoading = false;
      return;
    }

    this.updateStageDimensions();
    if (!this.stageWidth || !this.stageHeight) {
      this.pendingFile = file;
      this.isLoading = false;
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    try {
      image.src = objectUrl;
      await image.decode();
    } catch {
      this.isLoading = false;
      this.activeModal.dismiss('load-failed');
      URL.revokeObjectURL(objectUrl);
      return;
    }

    URL.revokeObjectURL(objectUrl);

    this.sourceImage = image;
    this.originalWidth = image.naturalWidth;
    this.originalHeight = image.naturalHeight;
    this.captureRectangleTargetsFromInputs();
    this.applyLogoShapeTargets();
    await this.initializeTransparencyOption(file, image);
    this.canvasMode = this.defaultCanvasMode;
    this.initialCanvasMode = this.defaultCanvasMode;
    this.zoomValue = 1;
    this.imageLayout = this.buildInitialImageLayout(image);
    this.selection = this.buildInitialSelection();
    this.initialImageLayout = { ...this.imageLayout };
    this.initialSelection = { ...this.selection };
    this.moveMode = 'image';
    this.isEditorReady = true;
    this.isLoading = false;
    this.render();
  }

  /** Store parent rectangle targets once so square mode can restore them later. */
  private captureRectangleTargetsFromInputs(): void {
    if (!this.allowLogoShapeSwitch || this.rectangleTargetWidth) {
      return;
    }

    this.rectangleTargetWidth = this.targetWidth;
    this.rectangleTargetHeight = this.targetHeight;
    this.rectangleAspectRatio = this.aspectRatio;
  }

  /** Apply square or rectangle export dimensions inside the cropper (client page unchanged). */
  private applyLogoShapeTargets(): void {
    if (!this.allowLogoShapeSwitch || this.roundCropper) {
      return;
    }

    if (this.logoShapeMode === 'square') {
      this.targetWidth = this.logoSquareExportSize;
      this.targetHeight = this.logoSquareExportSize;
      this.aspectRatio = 1;
      return;
    }

    if (this.rectangleTargetWidth && this.rectangleTargetHeight) {
      this.targetWidth = this.rectangleTargetWidth;
      this.targetHeight = this.rectangleTargetHeight;
      this.aspectRatio = this.rectangleAspectRatio
        ?? this.rectangleTargetWidth / this.rectangleTargetHeight;
    }
  }

  /**
   * PNG uploads: sample alpha to default the checkbox when transparency is present.
   * Opaque PNGs hide the option until the user selects transparent canvas.
   */
  private async initializeTransparencyOption(file: File, image: HTMLImageElement): Promise<void> {
    this.isPngUpload = this.isPngFile(file);
    this.imageHasTransparency = false;
    this.keepLogoTransparency = false;

    if (!this.isPngUpload) {
      return;
    }

    this.imageHasTransparency = await this.detectImageTransparency(image);
    this.keepLogoTransparency = this.imageHasTransparency;
  }

  private isPngFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return file.type === 'image/png' || name.endsWith('.png');
  }

  /**
   * Scans alpha channel for any pixel below full opacity. Uses stepped sampling on
   * large images to keep load times acceptable while remaining reliable for logos.
   */
  private async detectImageTransparency(image: HTMLImageElement): Promise<boolean> {
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!width || !height) {
      return false;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      return false;
    }

    ctx.drawImage(image, 0, 0);
    const { data } = ctx.getImageData(0, 0, width, height);
    const pixelCount = width * height;
    const step = Math.max(1, Math.floor(pixelCount / 12000));

    for (let pixelIndex = 0; pixelIndex < pixelCount; pixelIndex += step) {
      if (data[pixelIndex * 4 + 3] < 255) {
        return true;
      }
    }

    return false;
  }

  private observeStageSize(): void {
    const workspaceEl = this.workspace?.nativeElement;
    if (!workspaceEl) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updateStageDimensions();
      if (this.pendingFile) {
        void this.tryLoadPendingImage();
      } else if (this.isEditorReady) {
        this.render();
      }
    });

    this.resizeObserver.observe(workspaceEl);
    this.updateStageDimensions();
  }

  private updateStageDimensions(): void {
    const workspaceEl = this.workspace?.nativeElement;
    const canvas = this.stageCanvas?.nativeElement;
    if (!workspaceEl || !canvas) {
      return;
    }

    const width = Math.max(1, Math.floor(workspaceEl.clientWidth));
    const height = Math.max(1, Math.floor(workspaceEl.clientHeight));
    this.stageWidth = width;
    this.stageHeight = height;
    this.devicePixelRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(width * this.devicePixelRatio);
    canvas.height = Math.floor(height * this.devicePixelRatio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
  }

  private buildInitialImageLayout(image: HTMLImageElement): ImageLayout {
    const maxWidth = this.stageWidth * 0.92;
    const maxHeight = this.stageHeight * 0.92;
    let width = image.naturalWidth;
    let height = image.naturalHeight;

    if (width > maxWidth || height > maxHeight) {
      const scale = Math.min(maxWidth / width, maxHeight / height);
      width *= scale;
      height *= scale;
    }

    return {
      x: (this.stageWidth - width) / 2,
      y: (this.stageHeight - height) / 2,
      width,
      height,
      rotation: 0,
      scale: 1
    };
  }

  private buildInitialSelection(): CropRect {
    const frame = this.getDisplayCropFrame();
    return {
      x: (this.stageWidth - frame.width) / 2,
      y: (this.stageHeight - frame.height) / 2,
      width: frame.width,
      height: frame.height
    };
  }

  /** Visual frame only; export always uses targetWidth x targetHeight. */
  private getDisplayCropFrame(): { width: number; height: number } {
    const ratio = this.getAspectRatio();
    const isSmall = this.isBelowRecommendedResolution;

    // Tiny square targets (favicon): enlarge selection for usability only.
    // Larger targets (e.g. 280x80 logos, 250x250 square logos) use 1:1 selection.
    if (this.effectiveTargetWidth && this.effectiveTargetHeight) {
      const minSide = Math.min(this.effectiveTargetWidth, this.effectiveTargetHeight);
      if (Math.abs(ratio - 1) < 0.01 && minSide <= 64) {
        const side = isSmall ? 80 : 112;
        return { width: side, height: side };
      }

      return {
        width: this.effectiveTargetWidth,
        height: this.effectiveTargetHeight
      };
    }

    if (Math.abs(ratio - 1) < 0.01) {
      const side = isSmall ? 80 : 160;
      return { width: side, height: side };
    }

    return { width: 280, height: 80 };
  }

  private getAspectRatio(): number {
    if (this.aspectRatio) {
      return this.aspectRatio;
    }
    if (this.effectiveTargetWidth && this.effectiveTargetHeight) {
      return this.effectiveTargetWidth / this.effectiveTargetHeight;
    }
    return 1;
  }

  private observeThemeChanges(): void {
    this.themeObserver = new MutationObserver(() => {
      if (this.isEditorReady) {
        this.render();
      }
    });

    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  }

  private isCanvasDark(): boolean {
    return this.canvasMode === 'dark';
  }

  /** Editor stage background — solid for light/dark; transparent uses workspace checkerboard. */
  private getStageFillColor(): string | null {
    switch (this.canvasMode) {
      case 'dark':
        return '#1b1b1b';
      case 'transparent':
        return null;
      case 'light':
      default:
        return '#f2f2f2';
    }
  }

  /** Sidebar preview void fill — always reflects canvas mode (visualization only). */
  private getPreviewVoidFillColor(): string | null {
    switch (this.canvasMode) {
      case 'dark':
        return '#161616';
      case 'transparent':
        return null;
      case 'light':
      default:
        return '#ffffff';
    }
  }

  /** Export void fill when transparency is not preserved. */
  private getExportVoidColor(): string {
    return this.isCanvasDark() ? '#161616' : '#ffffff';
  }

  private render(): void {
    const canvas = this.stageCanvas?.nativeElement;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !this.stageWidth || !this.stageHeight) {
      return;
    }

    ctx.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
    ctx.clearRect(0, 0, this.stageWidth, this.stageHeight);

    const stageFill = this.getStageFillColor();
    if (stageFill) {
      ctx.fillStyle = stageFill;
      ctx.fillRect(0, 0, this.stageWidth, this.stageHeight);
    }

    this.paintImage(ctx);

    if (this.moveMode === 'selection') {
      this.paintSelectionDimming(ctx);
    }

    this.paintSelectionFrame(ctx);

    if (this.moveMode === 'image') {
      this.paintImageResizeHandles(ctx);
    }

    // Heavy sidebar preview during drag causes visible stage jitter — update after release.
    if (!this.isDragging) {
      this.schedulePreviewUpdate();
    }
  }

  private schedulePreviewUpdate(): void {
    if (this.previewUpdateRafId !== null) {
      return;
    }

    this.previewUpdateRafId = requestAnimationFrame(() => {
      this.previewUpdateRafId = null;
      this.updatePreview();
    });
  }

  private cancelScheduledPreviewUpdate(): void {
    if (this.previewUpdateRafId !== null) {
      cancelAnimationFrame(this.previewUpdateRafId);
      this.previewUpdateRafId = null;
    }
  }

  private updatePreview(): void {
    const canvas = this.previewCanvas?.nativeElement;
    if (!canvas || !this.isEditorReady) {
      return;
    }

    const targetWidth = this.effectiveTargetWidth || Math.max(1, Math.round(this.selection.width));
    const targetHeight = this.effectiveTargetHeight || Math.max(1, Math.round(this.selection.height));
    const { width: outputWidth, height: outputHeight } = this.getPreviewCanvasSize(
      targetWidth,
      targetHeight
    );

    // Preview always shows the selected canvas background; export honors keep-transparency.
    this.renderCroppedOutput(canvas, outputWidth, outputHeight, false, true);

    if (!this.isDragging) {
      this.scheduleFinalSizeUpdate();
    }
  }

  /** Render preview at CSS frame size × devicePixelRatio for a crisp sidebar image. */
  private getPreviewCanvasSize(
    targetWidth: number,
    targetHeight: number
  ): { width: number; height: number } {
    const frame = this.previewCanvas?.nativeElement?.parentElement as HTMLElement | null;
    const dpr = this.devicePixelRatio || window.devicePixelRatio || 1;

    if (frame?.clientWidth && frame?.clientHeight) {
      return {
        width: Math.max(1, Math.round(frame.clientWidth * dpr)),
        height: Math.max(1, Math.round(frame.clientHeight * dpr))
      };
    }

    const scale = this.roundCropper ? Math.max(4, Math.ceil(dpr * 2)) : Math.max(2, Math.ceil(dpr));
    return {
      width: Math.max(1, Math.round(targetWidth * scale)),
      height: Math.max(1, Math.round(targetHeight * scale))
    };
  }

  private paintImage(ctx: CanvasRenderingContext2D): void {
    if (!this.sourceImage) {
      return;
    }

    const { x, y, width, height, rotation, scale } = this.imageLayout;
    const centerX = x + width / 2;
    const centerY = y + height / 2;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(scale, scale);
    ctx.drawImage(
      this.sourceImage,
      0,
      0,
      this.sourceImage.naturalWidth,
      this.sourceImage.naturalHeight,
      -width / 2,
      -height / 2,
      width,
      height
    );
    ctx.restore();
  }

  private paintSelectionDimming(ctx: CanvasRenderingContext2D): void {
    const dimColor = this.isCanvasDark() ? 'rgba(0, 0, 0, 0.45)' : 'rgba(0, 0, 0, 0.18)';
    const { x, y, width, height } = this.selection;

    ctx.save();
    ctx.fillStyle = dimColor;
    ctx.fillRect(0, 0, this.stageWidth, y);
    ctx.fillRect(0, y, x, height);
    ctx.fillRect(x + width, y, this.stageWidth - x - width, height);
    ctx.fillRect(0, y + height, this.stageWidth, this.stageHeight - y - height);
    ctx.restore();
  }

  private paintSelectionFrame(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.selection;
    ctx.save();
    ctx.strokeStyle = '#00b0f0';
    ctx.lineWidth = 2;

    if (this.roundCropper) {
      const radius = Math.min(width, height) / 2;
      const centerX = x + width / 2;
      const centerY = y + height / 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.strokeRect(x, y, width, height);
    }

    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
    ctx.lineWidth = 1;

    if (this.roundCropper) {
      const radius = Math.min(width, height) / 2;
      const centerX = x + width / 2;
      const centerY = y + height / 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.strokeRect(x + 1, y + 1, width - 2, height - 2);
    }

    ctx.restore();
  }

  private scheduleFinalSizeUpdate(): void {
    if (this.finalSizeUpdateTimer) {
      clearTimeout(this.finalSizeUpdateTimer);
    }

    this.finalSizeUpdateTimer = setTimeout(() => {
      void this.updateFinalSizeLabel();
    }, 400);
  }

  private async updateFinalSizeLabel(): Promise<void> {
    const blob = await this.exportCroppedBlob();
    if (blob) {
      this.finalImageSizeLabel = this.formatFileSize(blob.size);
    }
  }

  /**
   * Maps the stage selection into the output canvas and paints from the original
   * source image (single resample). Avoids the blur from stage-buffer → export.
   */
  private renderCroppedOutput(
    canvas: HTMLCanvasElement,
    outputWidth: number,
    outputHeight: number,
    applyRoundMask: boolean,
    forPreview = false
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx || !this.sourceImage || !this.selection.width || !this.selection.height) {
      return;
    }

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    if (forPreview) {
      // Preview: always show canvas mode behind the image (even when export keeps alpha).
      const previewFill = this.getPreviewVoidFillColor();
      if (previewFill === null) {
        ctx.clearRect(0, 0, outputWidth, outputHeight);
      } else {
        ctx.fillStyle = previewFill;
        ctx.fillRect(0, 0, outputWidth, outputHeight);
      }
    } else if (this.isTransparencyPreserved) {
      // Export: preserve alpha when the user opted in.
      ctx.clearRect(0, 0, outputWidth, outputHeight);
    } else {
      ctx.fillStyle = this.getExportVoidColor();
      ctx.fillRect(0, 0, outputWidth, outputHeight);
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const selectionCenterX = this.selection.x + this.selection.width / 2;
    const selectionCenterY = this.selection.y + this.selection.height / 2;
    const scaleX = outputWidth / this.selection.width;
    const scaleY = outputHeight / this.selection.height;

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, outputWidth, outputHeight);
    ctx.clip();
    ctx.translate(outputWidth / 2, outputHeight / 2);
    ctx.scale(scaleX, scaleY);
    ctx.translate(-selectionCenterX, -selectionCenterY);
    this.paintImage(ctx);
    ctx.restore();

    if (this.roundCropper && applyRoundMask) {
      this.applyRoundMask(ctx, outputWidth, outputHeight);
    }
  }

  /**
   * Renders a large intermediate from the source image, then progressive-downsample
   * to the true target size. Looks sharper than a single jump to 56x56 / 280x80.
   * Round appearance is applied in CSS (same as logos), not baked into the file —
   * canvas circle masks soften small favicons and caused the hazy client preview.
   */
  private createHighQualityExportCanvas(targetWidth: number, targetHeight: number): HTMLCanvasElement {
    const exportScale = this.getExportRenderScale(targetWidth, targetHeight);
    const intermediate = document.createElement('canvas');
    this.renderCroppedOutput(
      intermediate,
      Math.max(1, Math.round(targetWidth * exportScale)),
      Math.max(1, Math.round(targetHeight * exportScale)),
      false
    );

    return this.progressiveDownscale(intermediate, targetWidth, targetHeight);
  }

  private getExportRenderScale(targetWidth: number, targetHeight: number): number {
    const minSide = Math.min(targetWidth, targetHeight);
    // Small outputs (favicon) need a larger intermediate for clean downscale.
    if (minSide <= 64) {
      return 8;
    }
    if (minSide <= 160) {
      return 4;
    }
    return 3;
  }

  private progressiveDownscale(
    source: HTMLCanvasElement,
    targetWidth: number,
    targetHeight: number
  ): HTMLCanvasElement {
    let current = source;

    while (current.width > targetWidth * 2 || current.height > targetHeight * 2) {
      const nextWidth = Math.max(targetWidth, Math.ceil(current.width / 2));
      const nextHeight = Math.max(targetHeight, Math.ceil(current.height / 2));
      current = this.drawScaledCanvas(current, nextWidth, nextHeight);
    }

    if (current.width !== targetWidth || current.height !== targetHeight) {
      current = this.drawScaledCanvas(current, targetWidth, targetHeight);
    }

    return current;
  }

  private drawScaledCanvas(
    source: HTMLCanvasElement,
    width: number,
    height: number
  ): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return source;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, width, height);
    return canvas;
  }

  /** Final export samples the original image into the configured target size. */
  private exportCroppedBlob(): Promise<Blob | null> {
    return new Promise((resolve) => {
      const targetWidth = this.effectiveTargetWidth || Math.max(1, Math.round(this.selection.width));
      const targetHeight = this.effectiveTargetHeight || Math.max(1, Math.round(this.selection.height));
      const outputCanvas = this.createHighQualityExportCanvas(targetWidth, targetHeight);
      const quality = this.outputFormat === 'jpeg' ? 0.95 : 1;
      outputCanvas.toBlob((blob) => resolve(blob), this.mimeType, quality);
    });
  }

  private applyRoundMask(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = width;
    maskCanvas.height = height;
    const maskCtx = maskCanvas.getContext('2d');

    if (!maskCtx) {
      return;
    }

    maskCtx.fillStyle = '#000000';
    maskCtx.beginPath();
    maskCtx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
    maskCtx.fill();

    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(maskCanvas, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
  }

  private getImageCorners(layout: ImageLayout = this.imageLayout): Record<ImageResizeHandle, Point> {
    const { x, y, width, height, rotation, scale } = layout;
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    const halfWidth = (width * scale) / 2;
    const halfHeight = (height * scale) / 2;
    const radians = (rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    const localCorners: Array<[ImageResizeHandle, Point]> = [
      ['nw', { x: -halfWidth, y: -halfHeight }],
      ['ne', { x: halfWidth, y: -halfHeight }],
      ['se', { x: halfWidth, y: halfHeight }],
      ['sw', { x: -halfWidth, y: halfHeight }]
    ];

    const corners = {} as Record<ImageResizeHandle, Point>;
    for (const [handle, corner] of localCorners) {
      corners[handle] = {
        x: centerX + corner.x * cos - corner.y * sin,
        y: centerY + corner.x * sin + corner.y * cos
      };
    }

    return corners;
  }

  private worldToImageLocal(point: Point, layout: ImageLayout): Point {
    const { x, y, width, height, rotation, scale } = layout;
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    const dx = point.x - centerX;
    const dy = point.y - centerY;
    const radians = (-rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const rotatedX = dx * cos - dy * sin;
    const rotatedY = dx * sin + dy * cos;

    return {
      x: rotatedX / scale,
      y: rotatedY / scale
    };
  }

  private resizeImageFromHandle(
    handle: ImageResizeHandle,
    point: Point,
    origin: ImageLayout
  ): ImageLayout {
    const aspect = origin.width / origin.height;
    const local = this.worldToImageLocal(point, origin);
    const originCorners = this.getImageCorners(origin);
    const anchor = this.getOppositeHandleCorner(handle, originCorners);

    const minHalf = this.minImageDisplaySize / 2;
    let halfWidth = minHalf;
    let halfHeight = minHalf;

    switch (handle) {
      case 'se':
        halfWidth = Math.max(minHalf, local.x);
        halfHeight = Math.max(minHalf, local.y);
        break;
      case 'nw':
        halfWidth = Math.max(minHalf, -local.x);
        halfHeight = Math.max(minHalf, -local.y);
        break;
      case 'ne':
        halfWidth = Math.max(minHalf, local.x);
        halfHeight = Math.max(minHalf, -local.y);
        break;
      case 'sw':
        halfWidth = Math.max(minHalf, -local.x);
        halfHeight = Math.max(minHalf, local.y);
        break;
    }

    if (halfWidth / halfHeight > aspect) {
      halfHeight = halfWidth / aspect;
    } else {
      halfWidth = halfHeight * aspect;
    }

    const newWidth = halfWidth * 2;
    const newHeight = halfHeight * 2;
    const newCenter = this.getCenterFromAnchorAndHandle(anchor, handle, newWidth, newHeight, origin.rotation, 1);
    const newTopLeft = {
      x: newCenter.x - newWidth / 2,
      y: newCenter.y - newHeight / 2
    };

    return {
      x: newTopLeft.x,
      y: newTopLeft.y,
      width: newWidth,
      height: newHeight,
      rotation: origin.rotation,
      scale: 1
    };
  }

  private getOppositeHandleCorner(
    handle: ImageResizeHandle,
    corners: Record<ImageResizeHandle, Point>
  ): Point {
    const opposite: Record<ImageResizeHandle, ImageResizeHandle> = {
      nw: 'se',
      ne: 'sw',
      se: 'nw',
      sw: 'ne'
    };
    return corners[opposite[handle]];
  }

  private getCenterFromAnchorAndHandle(
    anchor: Point,
    handle: ImageResizeHandle,
    width: number,
    height: number,
    rotation: number,
    scale: number
  ): Point {
    const halfWidth = (width * scale) / 2;
    const halfHeight = (height * scale) / 2;
    const radians = (rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const localVector: Point = {
      nw: { x: -halfWidth, y: -halfHeight },
      ne: { x: halfWidth, y: -halfHeight },
      se: { x: halfWidth, y: halfHeight },
      sw: { x: -halfWidth, y: halfHeight }
    }[handle];

    return {
      x: anchor.x + localVector.x * cos - localVector.y * sin,
      y: anchor.y + localVector.x * sin + localVector.y * cos
    };
  }

  private hitTestImageResizeHandle(point: Point): ImageResizeHandle | null {
    const corners = this.getImageCorners();
    const handles: ImageResizeHandle[] = ['nw', 'ne', 'se', 'sw'];

    for (const handle of handles) {
      const corner = corners[handle];
      const dx = point.x - corner.x;
      const dy = point.y - corner.y;
      if (Math.hypot(dx, dy) <= this.imageHandleHitRadius) {
        return handle;
      }
    }

    return null;
  }

  private isPointOnImage(point: Point): boolean {
    const local = this.worldToImageLocal(point, this.imageLayout);
    const halfWidth = this.imageLayout.width / 2;
    const halfHeight = this.imageLayout.height / 2;
    return Math.abs(local.x) <= halfWidth && Math.abs(local.y) <= halfHeight;
  }

  private paintImageResizeHandles(ctx: CanvasRenderingContext2D): void {
    const corners = this.getImageCorners();
    const size = this.imageHandleSize;
    const half = size / 2;

    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#00b0f0';
    ctx.lineWidth = 2;

    for (const handle of ['nw', 'ne', 'se', 'sw'] as ImageResizeHandle[]) {
      const corner = corners[handle];
      ctx.fillRect(corner.x - half, corner.y - half, size, size);
      ctx.strokeRect(corner.x - half, corner.y - half, size, size);
    }

    ctx.restore();
  }

  private getCanvasPoint(event: PointerEvent): { x: number; y: number } {
    const canvas = this.stageCanvas?.nativeElement;
    const rect = canvas?.getBoundingClientRect();

    if (!rect) {
      return { x: 0, y: 0 };
    }

    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  private isPointInSelection(point: { x: number; y: number }): boolean {
    const { x, y, width, height } = this.selection;

    if (this.roundCropper) {
      const radius = Math.min(width, height) / 2;
      const centerX = x + width / 2;
      const centerY = y + height / 2;
      const dx = point.x - centerX;
      const dy = point.y - centerY;
      return dx * dx + dy * dy <= radius * radius;
    }

    return point.x >= x && point.x <= x + width && point.y >= y && point.y <= y + height;
  }

  private endDrag(): void {
    const wasDragging = this.isDragging;
    this.isDragging = false;
    this.dragState = null;
    this.activeResizeHandle = null;

    if (wasDragging) {
      this.cancelScheduledStageRender();
      this.render();
      this.cancelScheduledPreviewUpdate();
      this.updatePreview();
    }
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private formatFileSize(size: number): string {
    if (!size) {
      return '-';
    }

    const mb = size / 1024 / 1024;
    if (mb >= 1) {
      return `${mb.toFixed(2)} MB`;
    }

    return `${(size / 1024).toFixed(2)} KB`;
  }
}
